import { Observable, of, defer, EMPTY } from "rxjs"
import { switchMap, map, catchError, filter, finalize } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { Router } from "../../../../server/router"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    CLOSE_MODIFIER_GROUP_FORM_CLICKED,
    DELETE_MODIFIER_GROUP_CLICKED,
    MODIFIER_GROUP_DATA_CREATED,
    MODIFIER_GROUP_DATA_UPDATED,
    MODIFIER_GROUP_FORM_SUBMITTED,
    modifierGroupDataCreated,
    modifierGroupDataUpdated,
    modifierGroupLocationSaveSuccess,
    modifierGroupSaveSuccess,
} from "./actions"
import { assignModifierGroupToProduct, createActionLoadingError } from "../../../data/actions"
import {
    composeModifierGroupAddedRequestData,
    composeModifierGroupFromAddPayload,
    composeModifierGroupFromUpdatePayload,
    composeModifierGroupOverrideFromUpdatePayload,
    composeModifierGroupUpdateRequestData,
} from "./utlis"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import { getCurrentPath } from "../../../router/selectors"
import {
    createActionItemsAndCategoriesAgendaModifierGroupsLoaded,
    invalidateMenuData,
    ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
    MENU_DATA_INVALIDATED,
    shouldRefetchMenuDataEntity,
} from "../actions"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { getHttpHeaders, getSelectedAccountId } from "../../../auth/selectors"
import { fetchModifierGroups, modifierGroupDelete } from "./request"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import { EventDataResponse } from "../../../../graphql/types"
import { shouldDownloadItemsAndCategoriesData } from "../selectors"
import { shouldAddNewGroupToItem } from "../modifiers/selectors"
import { getModifierGroupById } from "./selectors"
import { createEventRequest, requestEvent } from "../../../../events/utils"
import { EventType } from "../../../../events/types"
import {
    closeLoadingNotification,
    NotificationType,
    openLoadingNotification,
    openNotification,
} from "../../../uiKit/Notification/Notification"
import * as uuid from "uuid"
import type { ModifierGroupFormItem } from "./ModifierGroupForm"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../../layouts/header/actions"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const modifierGroupSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_GROUP_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("modifierGroupSubmittedEpics - form submitted", {
                data: action.payload,
            })
            return [
                action.payload.oldValue
                    ? modifierGroupDataUpdated(action.payload.oldValue, action.payload.newValue)
                    : modifierGroupDataCreated(action.payload.oldValue, action.payload.newValue),
            ]
        })
    )

export const modifierGroupCreatedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_GROUP_DATA_CREATED),
        switchMap((action: Action) => {
            const accountId = getSelectedAccountId(deps.getState())
            const formValues: ModifierGroupFormItem = action.payload.newValue
            const payload = {
                ...composeModifierGroupAddedRequestData(formValues, null),
                auditData: constructAuditData(deps, true),
            }

            openLoadingNotification()
            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.ModifierGroupAddRequested,
                        {
                            accountId: accountId as string,
                            correlationId: uuid.v7(),
                        },
                        payload
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("modifierGroupCreatedEpics - id is missing (adding)")
                    }
                    openNotification(NotificationType.Success, "Modifier Group created")

                    // Fire actions for updating redux
                    const groupData = composeModifierGroupFromAddPayload(payload, formValues, deps)
                    // Do not invalidate here: the open item detail must stay cached or its form
                    // unmounts and the pending assignment is lost.
                    if (shouldAddNewGroupToItem(deps.getState())) {
                        return [assignModifierGroupToProduct(groupData.id), modifierGroupSaveSuccess(groupData)]
                    }

                    return [modifierGroupSaveSuccess(groupData), invalidateMenuData(["modifiers", "itemDetails"])]
                }),
                catchError(e => {
                    logger.error("modifierGroupCreatedEpics - could not create modifier group", null, e)
                    openNotification(
                        NotificationType.Error,
                        "Could not create Modifier Group",
                        "Please try again later"
                    )
                    return of()
                }),
                finalize(() => {
                    closeLoadingNotification()
                    return EMPTY
                })
            )
        })
    )

export const modifierGroupUpdatedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_GROUP_DATA_UPDATED),
        switchMap((action: Action) => {
            const accountId = getSelectedAccountId(deps.getState())
            const selectedLocationId = getSelectedLocationOverrideId(deps.getState())
            const prevValues: ModifierGroupFormItem = action.payload.oldValue
            const formValues: ModifierGroupFormItem = action.payload.newValue

            // Updating
            const payload = {
                ...composeModifierGroupUpdateRequestData(formValues, prevValues, selectedLocationId),
                auditData: constructAuditData(deps, true),
            }
            openLoadingNotification()
            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.ModifierGroupUpdateRequested,
                        {
                            accountId: accountId as string,
                            correlationId: uuid.v7(),
                        },
                        payload
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("modifierGroupUpdatedEpics - id is missing (update)")
                    }
                    openNotification(NotificationType.Success, "Modifier Group updated")

                    // Fire actions for updating redux
                    if (selectedLocationId) {
                        const originGroup = getModifierGroupById(deps.getState(), payload.groupId)
                        const override = composeModifierGroupOverrideFromUpdatePayload(
                            payload,
                            originGroup,
                            selectedLocationId
                        )
                        return [modifierGroupLocationSaveSuccess(override, payload.groupId, selectedLocationId)]
                    }

                    const groupData = composeModifierGroupFromUpdatePayload(payload, formValues, deps)
                    // See the note in modifierGroupCreatedEpics.
                    if (shouldAddNewGroupToItem(deps.getState())) {
                        return [assignModifierGroupToProduct(groupData.id), modifierGroupSaveSuccess(groupData)]
                    }

                    return [modifierGroupSaveSuccess(groupData), invalidateMenuData(["modifiers", "itemDetails"])]
                }),
                catchError(e => {
                    logger.error("modifierGroupUpdatedEpics - could not update modifier group", null, e)
                    openNotification(
                        NotificationType.Error,
                        "Could not update Modifier Group",
                        "Please try again later"
                    )
                    return of()
                }),
                finalize(() => {
                    closeLoadingNotification()
                    return EMPTY
                })
            )
        })
    )

export const closeModifierGroupDetailEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MODIFIER_GROUP_FORM_SUBMITTED, CLOSE_MODIFIER_GROUP_FORM_CLICKED, DELETE_MODIFIER_GROUP_CLICKED),
        switchMap((action: Action) => {
            const currentUrlQuery = getCurrentPath(deps.getState()).query
            const categoryId = currentUrlQuery.id
            const tab = currentUrlQuery.tab

            if (action.type === "modifier-group/close/clicked" && tab === "categories" && categoryId) {
                Router.push(`/menu/categories/edit/${categoryId}`)
                return [assignModifierGroupToProduct(null)]
            }

            if (action.type === "modifier-group/close/clicked" && tab === "schematized" && categoryId) {
                Router.push(`/menu/categories/edit/${categoryId}`)
                return [assignModifierGroupToProduct(null)]
            }

            if (tab === "categories" && categoryId) {
                Router.push(`/menu/categories/edit/${categoryId}`)
                return []
            } else if (tab === "categories" && !categoryId) {
                Router.push("/menu/categories/add/product")
                return []
            }

            if (tab === "schematized" && categoryId) {
                Router.push(`/sales-input/schematized/edit/${categoryId}`)
                return []
            }

            Router.push("/menu/modifier-groups")
            return []
        })
    )

export const deleteModifierGroupSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_MODIFIER_GROUP_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteModifierGroupSubmittedEpics - form submitted", {
                data: action.payload,
            })
            return defer(() =>
                modifierGroupDelete(
                    getHttpHeaders(deps.getState()),
                    action.payload.modifierGroupId,
                    constructAuditData(deps, true)
                )
            ).pipe(
                switchMap(() => [invalidateMenuData(["modifiers", "itemDetails"])]),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export const mounted1Epic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "modifierGroups")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        map(() => createLoadingStarted("modifierGroups"))
    )

export const mounted2Epic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            ITEMS_CATEGORIES_AGENDA_WILL_MOUNT,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            LOAD_AUTH_LOCATIONS_SUCCESS,
            MENU_DATA_INVALIDATED
        ),
        filter((action: Action) => shouldRefetchMenuDataEntity(action, "modifierGroups")),
        filter(() => shouldDownloadItemsAndCategoriesData(deps.getState())),
        switchMap(() =>
            defer(() => fetchModifierGroups(getHttpHeaders(deps.getState()))).pipe(
                switchMap(result => {
                    if (!result) {
                        return [createLoadingEnded("modifierGroups")]
                    }

                    return [
                        createLoadingEnded("modifierGroups"),
                        createActionItemsAndCategoriesAgendaModifierGroupsLoaded(result),
                    ]
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        )
    )

export default [
    mounted1Epic,
    mounted2Epic,
    closeModifierGroupDetailEpics,
    modifierGroupSubmittedEpics,
    deleteModifierGroupSubmittedEpics,
    modifierGroupUpdatedEpics,
    modifierGroupCreatedEpics,
]
